import React, { useEffect, useMemo, useRef, useState } from "react";

import ProductsAndCoverageHeader from "components/ProductsAndCoverageHeader";
import CoverageAreaMap from "components/CoverageAreaMap";
import CardProductCovered from "components/CardProductCovered";
import WorkingHour from "components/WorkingHour";
import NameStatusCard from "components/NameStatusCard";
import CoverageAreaQRCode from "components/CoverageAreaQRCode";

import {
  handleGetWorkingAreaDetailRequest,
  handleGetWorkingAreasRequest,
  handleUpdateWorkingHourRequest,
} from "store/modules/Logistics/thunk.store";
import { useAppDispatch, useAppSelector } from "store/hooks";

import {
  handleGetCatalogAreaRequest,
  handleGetCatalogProductsRequest,
} from "store/modules/Catalog/thunk.store";

import Grid from "@mui/material/Grid";

import AccordionProductCovered from "components/AccordionProductCovered";
import { useTranslation } from "react-i18next";
import { TabsNavigation } from "ctech-react-components";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import * as Styled from "./styles";
import {
  getMassEditPricesTextButtonSx,
  massEditWorkingHoursTextButtonSx,
} from "./muiSx";
import CardDriverArea from "components/CardDriverArea";
import { isDealerStoreType, isStorePortariaType } from "types/coverageAreaType";
import {
  CoverageAreaProps,
  WorkingHourProps,
} from "store/modules/Logistics/types.store";

import {
  Checkbox,
  Typography,
  Button as MuiButton,
  Modal,
} from "@mui/material";
import Button from "components/atoms/Button";
import DrawerMenu from "components/DrawerMenu";
import MassEditPriceMenu from "./components/MassEditPriceMenu";
import MassEditWorkingHoursMenu from "./components/MassEditWorkingHoursMenu";
import { toast } from "utils/toast";
import { useDealerDrivers } from "./useDealerDrivers";

type DealerLocationState = {
  name?: string;
  gasDoPovo?: boolean;
};

const LIST_TAB_VALUE = "initial";

const ProductsAndCoverage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { dealerId = "", coverageAreaId } = useParams<{
    dealerId: string;
    coverageAreaId?: string;
  }>();
  const dealerState = (location.state as DealerLocationState | null) ?? {};

  const dealer = {
    id: dealerId,
    name: dealerState.name ?? "",
    gasDoPovo: dealerState.gasDoPovo,
  };

  const currentTabValue = coverageAreaId ?? LIST_TAB_VALUE;

  const dispatch = useAppDispatch();
  const { workingAreas } = useAppSelector((state) => state.logistics);
  const { coverageArea } = useAppSelector((state) => state.catalog);

  const [isMassEdit, setIsMassEdit] = useState(false);
  const [isMassEditPrice, setIsMassEditPrice] = useState(false);
  const [isMassEditWorkingHours, setIsMassEditWorkingHours] = useState(false);
  const [massEditData, setMassEditData] = useState<CoverageAreaProps[]>([]);
  const [isCancelEditModalOpen, setIsCancelEditModalOpen] = useState(false);
  const [isHeaderVisible, setIsHeaderVisible] = useState(true);
  const [tabsHeight, setTabsHeight] = useState(0);
  const headerRef = useRef<HTMLDivElement>(null);
  const tabsNavigationRef = useRef<HTMLDivElement>(null);

  const navigateToCoverageArea = (areaId?: string) => {
    const basePath = `/dealers/${dealerId}/products-and-coverage`;
    navigate(areaId ? `${basePath}/${areaId}` : basePath, {
      state: location.state,
    });
  };

  const handleChangeTab = async (
    _: React.SyntheticEvent | null,
    newValue: string,
  ) => {
    if (newValue === LIST_TAB_VALUE) {
      navigateToCoverageArea();
      return;
    }
    navigateToCoverageArea(newValue);
  };

  const Tabs = useMemo(() => {
    const initialTabs = [{ label: "Todas", value: LIST_TAB_VALUE }];
    workingAreas?.forEach((workingArea) =>
      initialTabs.push({ label: workingArea.name, value: workingArea.id }),
    );
    return initialTabs;
  }, [workingAreas]);

  const currentCoverageArea = useMemo(() => {
    if (!coverageAreaId) {
      return null;
    }
    return (
      workingAreas?.find((workingArea) => workingArea.id === coverageAreaId) ||
      null
    );
  }, [workingAreas, coverageAreaId]);

  const shouldLoadDealerDrivers =
    Boolean(currentCoverageArea) &&
    !isDealerStoreType(currentCoverageArea?.type);

  const { dealerDrivers } = useDealerDrivers({
    dealerId,
    shouldLoad: shouldLoadDealerDrivers,
  });

  const gasDoPovo = !!dealer?.gasDoPovo;

  useEffect(() => {
    if (!dealerId) {
      return;
    }
    dispatch(handleGetWorkingAreasRequest({ userId: dealerId }));
    dispatch(handleGetCatalogProductsRequest());
  }, [dispatch, dealerId]);

  useEffect(() => {
    if (!dealerId || !coverageAreaId) {
      return;
    }

    dispatch(
      handleGetWorkingAreaDetailRequest({
        dealerId,
        coverageAreaId,
      }),
    );
    dispatch(
      handleGetCatalogAreaRequest({
        coverageAreaId,
        userId: dealerId,
      }),
    );
  }, [dispatch, dealerId, coverageAreaId]);

  const handleUpdateWorkingArea = async (
    openingHours: WorkingHourProps[],
    holidayOpeningHours?: any[],
  ) => {
    await dispatch(
      handleUpdateWorkingHourRequest({
        coverageAreaId: currentCoverageArea?.id || "",
        openingHours,
        dealerId: currentCoverageArea?.dealer?.id || dealerId,
        holidayOpeningHours: [holidayOpeningHours],
      }),
    ).unwrap();
  };

  const handleMassEdit = () => {
    if (isMassEdit) {
      setIsCancelEditModalOpen(true);
    } else {
      setIsMassEdit(true);
      setIsCancelEditModalOpen(false);
    }
  };

  const handleGetCoverageAreaProductsAndPrices = async (
    selectedCoverageAreaId: string,
  ) => {
    try {
      const coverageAreaProductsAndPrices = await dispatch(
        handleGetCatalogAreaRequest({
          coverageAreaId: selectedCoverageAreaId,
          userId: dealerId,
        }),
      ).unwrap();
      return coverageAreaProductsAndPrices?.data?.productPrices || [];
    } catch {
      return [];
    }
  };

  const handleGetCoverageAreaDetail = async (
    selectedCoverageAreaId: string,
  ) => {
    try {
      return await dispatch(
        handleGetWorkingAreaDetailRequest({
          dealerId,
          coverageAreaId: selectedCoverageAreaId,
        }),
      ).unwrap();
    } catch {
      return null;
    }
  };

  const handleMassEditData = async (data: CoverageAreaProps) => {
    const [coverageAreaDetail, coverageAreaProductsAndPrices] =
      await Promise.all([
        handleGetCoverageAreaDetail(data.id),
        handleGetCoverageAreaProductsAndPrices(data.id),
      ]);

    const coverageAreaWithProductsAndPrices = {
      ...(coverageAreaDetail ?? data),
      productPrices: coverageAreaProductsAndPrices,
    };

    setMassEditData((prev: CoverageAreaProps[]) => {
      if (
        prev.some((item) => item.id === coverageAreaWithProductsAndPrices.id)
      ) {
        return prev.filter(
          (item) => item.id !== coverageAreaWithProductsAndPrices.id,
        );
      }
      return [...prev, coverageAreaWithProductsAndPrices];
    });
  };

  const handleSelectAll = async () => {
    if (massEditData.length > 0) {
      setMassEditData([]);
    } else {
      const workingAreasWithProductsAndPrices = await Promise.all(
        workingAreas?.map(async (workingArea) => {
          const [coverageAreaDetail, coverageAreaProductsAndPrices] =
            await Promise.all([
              handleGetCoverageAreaDetail(workingArea.id),
              handleGetCoverageAreaProductsAndPrices(workingArea.id),
            ]);
          return {
            ...(coverageAreaDetail ?? workingArea),
            productPrices: coverageAreaProductsAndPrices,
          };
        }) || [],
      );
      const enabledAreas = workingAreasWithProductsAndPrices.filter(
        (area) => area.enabled,
      );
      if (enabledAreas.length !== workingAreas?.length) {
        toast.warning("Existem áreas desativadas que não podem ser editadas!");
      }
      setMassEditData(enabledAreas);
    }
  };

  const handleCloseCancelEditModal = () => {
    setIsMassEdit(false);
    setIsCancelEditModalOpen(false);
    setMassEditData([]);
  };

  const handleEditWorkingHours = () => {
    if (isMassEditWorkingHours) {
      setIsMassEditWorkingHours(false);
      handleCloseCancelEditModal();
    } else {
      setIsMassEditWorkingHours(true);
    }
  };

  useEffect(() => {
    const updateTabsHeight = () => {
      if (tabsNavigationRef.current) {
        setTabsHeight(tabsNavigationRef.current.offsetHeight);
      }
    };
    updateTabsHeight();
    window.addEventListener("resize", updateTabsHeight);
    return () => window.removeEventListener("resize", updateTabsHeight);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      if (!headerRef.current) return;
      const headerRect = headerRef.current.getBoundingClientRect();
      const headerHeight = headerRect.height;
      const headerTop = headerRect.top;
      const is50PercentOut = headerTop + headerHeight * 0.8 < 0;
      setIsHeaderVisible(!is50PercentOut);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleEditPrices = () => {
    if (isMassEditPrice) {
      setIsMassEditPrice(false);
      handleCloseCancelEditModal();
    } else {
      setIsMassEditPrice(true);
    }
  };

  const renderCoverageAreaList = () => (
    <>
      <Styled.HeaderWrapper ref={headerRef} $isVisible={isHeaderVisible}>
        <ProductsAndCoverageHeader
          dealer={dealer}
          title={t("productsAndCoverage")}
          titleButton={t("newCoverageAreaButton")}
        />
      </Styled.HeaderWrapper>
      <Styled.MassEditContainer
        $isFixed={!isHeaderVisible}
        $tabsHeight={tabsHeight}
      >
        {isMassEdit && massEditData.length > 0 && (
          <Typography variant="body2" color="primary">
            {t("areasSelected", { count: massEditData.length })}
          </Typography>
        )}
        <Styled.MassEditButtonContainer>
          {isMassEdit && (
            <>
              <MuiButton
                disabled={massEditData.length === 0}
                variant="text"
                color={massEditData.length === 0 ? "inherit" : "primary"}
                onClick={handleEditWorkingHours}
                sx={massEditWorkingHoursTextButtonSx}
              >
                {t("editWorkingHours")}
              </MuiButton>
              <MuiButton
                disabled={massEditData.length === 0}
                variant="text"
                color={massEditData.length === 0 ? "inherit" : "primary"}
                onClick={handleEditPrices}
                sx={getMassEditPricesTextButtonSx(massEditData.length === 0)}
              >
                {t("editPrices")}
              </MuiButton>
            </>
          )}
          <Button
            variant={isMassEdit ? "contained" : "outlined"}
            color={isMassEdit ? "primary" : "inherit"}
            onClick={handleMassEdit}
          >
            {isMassEdit
              ? t("cancelEdit").toUpperCase()
              : t("editInBatch").toUpperCase()}
          </Button>
        </Styled.MassEditButtonContainer>
      </Styled.MassEditContainer>
      {isMassEdit && (
        <Styled.SelectAllContainer>
          <Checkbox
            checked={massEditData.length === workingAreas?.length}
            onChange={handleSelectAll}
          />
          <Typography variant="body2">{t("selectAll")}</Typography>
        </Styled.SelectAllContainer>
      )}

      <Grid
        container
        spacing={{ xs: 2, md: 3 }}
        columns={{ xs: 4, sm: 8, md: 12 }}
      >
        {workingAreas &&
          workingAreas?.map((product) => (
            <Grid item xs={6} md={6} sm={12} key={product.id}>
              <AccordionProductCovered
                handleOpenArea={(value: string) => {
                  return handleChangeTab(null, value);
                }}
                product={product}
                dealerSelected={product.id}
                isMassEdit={isMassEdit}
                handleMassEditData={handleMassEditData}
                massEditData={massEditData}
                gasDoPovo={gasDoPovo}
              />
            </Grid>
          ))}
      </Grid>
    </>
  );

  const renderCoverageAreaDetail = () => {
    if (!currentCoverageArea) {
      return null;
    }

    return (
      <Styled.Row>
        <Styled.Column>
          <NameStatusCard
            workingAreaData={currentCoverageArea}
            goBack={() => handleChangeTab(null, LIST_TAB_VALUE)}
            dealerId={dealer.id}
          />
          {!isDealerStoreType(currentCoverageArea?.type) && (
            <CardDriverArea
              currentCoverageArea={currentCoverageArea}
              dealerSelected={currentTabValue}
              dealerId={dealerId}
              dealerDrivers={dealerDrivers}
            />
          )}

          <CardProductCovered
            coverageArea={
              coverageArea?.find(
                (area) => area.coverageAreaId === currentTabValue,
              ) as any
            }
            dealerSelected={currentTabValue}
            gasDoPovo={gasDoPovo}
            workingAreaType={currentCoverageArea?.type}
          />
        </Styled.Column>

        <Styled.Column>
          {isStorePortariaType(currentCoverageArea.type) ? (
            <CoverageAreaQRCode
              coverageAreaURL={currentCoverageArea.storeUri}
              coverageAreaName={currentCoverageArea.name}
              currentCoverageArea={currentCoverageArea}
            />
          ) : (
            <CoverageAreaMap
              currentCoverageArea={currentCoverageArea}
              coverageAreaId={currentCoverageArea?.id}
              selectedAreaData={currentCoverageArea?.map?.geoJson}
              dealerId={dealer?.id}
            />
          )}
          <WorkingHour
            workingHourData={currentCoverageArea?.openingHours}
            onUpdate={handleUpdateWorkingArea}
            dealerSelected={currentTabValue}
          />
        </Styled.Column>
      </Styled.Row>
    );
  };

  return (
    <Styled.Container>
      <Styled.TabsNavigationWrapper ref={tabsNavigationRef}>
        <TabsNavigation
          onChangeTab={handleChangeTab}
          selectedTab={currentTabValue}
          tabs={Tabs}
        />
      </Styled.TabsNavigationWrapper>
      <Styled.Content>
        {currentTabValue === LIST_TAB_VALUE
          ? renderCoverageAreaList()
          : renderCoverageAreaDetail()}
      </Styled.Content>
      <Modal
        open={isCancelEditModalOpen}
        onClose={() => setIsCancelEditModalOpen(false)}
      >
        <Styled.CancelEditModalContainer>
          <Typography variant="h6">{t("cancelEditModalTitle")}</Typography>
          <Typography variant="body2">{t("cancelEditModalContent")}</Typography>
          <Styled.CancelEditModalButtonsContainer>
            <Button
              variant="outlined"
              color="inherit"
              onClick={() => setIsCancelEditModalOpen(false)}
            >
              {t("cancelEditModalCancel").toUpperCase()}
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleCloseCancelEditModal}
            >
              {t("cancelEditModalClose").toUpperCase()}
            </Button>
          </Styled.CancelEditModalButtonsContainer>
        </Styled.CancelEditModalContainer>
      </Modal>
      <DrawerMenu open={isMassEditPrice} onClose={handleEditPrices}>
        <MassEditPriceMenu
          massEditData={massEditData}
          handleCloseMassEditPrice={handleEditPrices}
          gasDoPovo={gasDoPovo}
        />
      </DrawerMenu>
      <DrawerMenu
        open={isMassEditWorkingHours}
        onClose={handleEditWorkingHours}
        width={605}
      >
        <MassEditWorkingHoursMenu
          massEditData={massEditData}
          handleCloseMassEditWorkingHours={handleEditWorkingHours}
        />
      </DrawerMenu>
    </Styled.Container>
  );
};

export default ProductsAndCoverage;
